import {
	startOfWeek,
	endOfWeek,
	startOfMonth,
	endOfMonth,
	startOfYear,
	endOfYear,
	subWeeks,
	subMonths,
} from 'date-fns';
import { DateRange as CustomDateRange } from 'react-day-picker';

export type DateRange = {
	from: Date;
	to: Date;
};

export const getToday = (): DateRange => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const endOfDay = new Date(today);
	endOfDay.setHours(23, 59, 59, 999);

	return {
		from: today,
		to: endOfDay,
	};
};

export const getYesterday = (): DateRange => {
	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1); // Go back 1 day
	yesterday.setHours(0, 0, 0, 0); // Start of yesterday
	const endOfDay = new Date(yesterday);
	endOfDay.setHours(23, 59, 59, 999); // End of yesterday

	return {
		from: yesterday,
		to: endOfDay,
	};
};

export const getThisWeek = (): DateRange => {
	const now = new Date();
	return {
		from: startOfWeek(now, { weekStartsOn: 1 }), // Monday
		to: endOfWeek(now, { weekStartsOn: 1 }), // Sunday
	};
};

export const getLastWeek = (): DateRange => {
	const now = new Date();
	const lastWeek = subWeeks(now, 1);
	return {
		from: startOfWeek(lastWeek, { weekStartsOn: 1 }), // Monday
		to: endOfWeek(lastWeek, { weekStartsOn: 1 }), // Sunday
	};
};

export const getThisMonth = (): DateRange => {
	const now = new Date();
	return {
		from: startOfMonth(now),
		to: endOfMonth(now),
	};
};

export const getLastMonth = (): DateRange => {
	const now = new Date();
	const lastMonth = subMonths(now, 1);
	return {
		from: startOfMonth(lastMonth),
		to: endOfMonth(lastMonth),
	};
};

export const getThisYear = (): DateRange => {
	const now = new Date();
	return {
		from: startOfYear(now),
		to: endOfYear(now),
	};
};

export const getCustomRange = (startDate: Date, endDate: Date): DateRange => {
	return {
		from: startDate,
		to: endDate,
	};
};

export const getDateRangeByKey = (key: string, customDates?: CustomDateRange): DateRange => {
	switch (key) {
		case 'today':
			return getToday();
		case 'yesterday':
			return getYesterday();
		case 'thisWeek':
			return getThisWeek();
		case 'lastWeek':
			return getLastWeek();
		case 'thisMonth':
			return getThisMonth();
		case 'lastMonth':
			return getLastMonth();
		case 'thisYear':
			return getThisYear();
		case 'customDate':
			return getCustomRange(customDates?.from || new Date(), customDates?.to || new Date());
		default:
			return getToday();
	}
};

/**
 * Formats a date for API consumption in WAT timezone without UTC conversion.
 * This prevents timezone shift issues where local dates get converted to UTC.
 *
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format in WAT timezone
 */
export const formatDateForAPI = (date: Date): string => {
	// Use local date values without UTC conversion to prevent timezone shifts
	const year = date.getFullYear();
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const getDateRangeForAPI = (
	key: string,
	customDates?: { from: Date; to: Date }
): { startDate: string; endDate: string } => {
	const range = getDateRangeByKey(key, customDates);
	return {
		startDate: formatDateForAPI(range.from),
		endDate: formatDateForAPI(range.to),
	};
};
